import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { TeamSharedContent } from './TeamShared';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Users, UserPlus, Shield, Crown, User, Mail, Copy, Check, Trash2,
  MoreVertical, RefreshCw, AlertTriangle, Clock, CreditCard, Building2,
  Edit2, X, Share2, Activity, ArrowRightLeft, DollarSign, ChevronRight,
  Cpu, Zap, Sparkles, Settings as SettingsIcon, Lock, Key, Plus,
  Palette, Eye, EyeOff,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/apiClient';

const OrgSettings = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'members';
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [editNameDialogOpen, setEditNameDialogOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [copiedLink, setCopiedLink] = useState(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  const [modelPolicy, setModelPolicy] = useState(null);
  const [modelPolicyLoading, setModelPolicyLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState('90');
  const [createdKey, setCreatedKey] = useState(null);
  const [keyLoading, setKeyLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [brandingDraft, setBrandingDraft] = useState({});
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('decoded_sidebar_collapsed') === 'true';
  });

  useEffect(() => {
    const handleSidebarToggle = (e) => setSidebarCollapsed(e.detail.collapsed);
    window.addEventListener('sidebarToggle', handleSidebarToggle);
    return () => window.removeEventListener('sidebarToggle', handleSidebarToggle);
  }, []);

  useEffect(() => { fetchOrgData(); }, []);

  const fetchOrgData = async () => {
    setLoading(true);
    try {
      const [orgData, membersData, invitesData, logData] = await Promise.all([
        apiGet('/api/org').catch(() => null),
        apiGet('/api/org/members').catch(() => null),
        apiGet('/api/org/invites').catch(() => null),
        apiGet('/api/org/activity-log?limit=20').catch(() => null),
      ]);

      if (orgData) {
        setOrg(orgData);
        setNewOrgName(orgData.name);
      }
      if (membersData) setMembers(membersData.members || []);
      if (invitesData) setInvites(invitesData.invites || []);
      if (logData) setActivityLog(logData.logs || []);

      // Fetch model policy
      try {
        const policyData = await apiGet('/api/org/model-policy');
        setModelPolicy(policyData);
      } catch { /* ignore */ }

      // Fetch API keys
      try {
        const d = await apiGet('/api/org/api-keys');
        setApiKeys(d.api_keys || []);
      } catch { /* ignore */ }

      // Fetch branding
      try {
        const d = await apiGet('/api/org/branding');
        setBranding(d);
        setBrandingDraft(d);
      } catch { /* ignore */ }
    } catch (err) {
      toast.error('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail) { toast.error('Please enter an email address'); return; }
    setInviteLoading(true);
    try {
      const data = await apiPost('/api/org/invite', { email: inviteEmail, role: inviteRole });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      fetchOrgData();
      if (data.invite_link) {
        toast.info(`Invite link: ${window.location.origin}${data.invite_link}`, { duration: 10000 });
      }
    } catch (err) { toast.error(err?.message || 'Failed to send invitation'); }
    finally { setInviteLoading(false); }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await apiDelete(`/api/org/invites/${inviteId}`);
      toast.success('Invitation cancelled');
      fetchOrgData();
    } catch (err) { toast.error(err?.message || 'Failed to cancel invitation'); }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      await apiPut(`/api/org/members/${memberId}/role`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      fetchOrgData();
    } catch (err) { toast.error(err?.message || 'Failed to update role'); }
  };

  const handleToggleBilling = async (memberId, hasBilling) => {
    try {
      await apiPut(`/api/org/members/${memberId}/billing`, { has_billing: !hasBilling });
      toast.success(`Billing ${!hasBilling ? 'enabled' : 'disabled'}`);
      fetchOrgData();
    } catch (err) { toast.error(err?.message || 'Failed to toggle billing'); }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!confirm(`Remove ${memberName} from the organization?`)) return;
    try {
      await apiDelete(`/api/org/members/${memberId}`);
      toast.success('Member removed');
      fetchOrgData();
    } catch (err) { toast.error(err?.message || 'Failed to remove member'); }
  };

  const handleUpdateOrgName = async () => {
    if (!newOrgName.trim()) { toast.error('Name cannot be empty'); return; }
    try {
      await apiPut('/api/org', { name: newOrgName });
      toast.success('Organization updated');
      setEditNameDialogOpen(false);
      fetchOrgData();
    } catch (err) { toast.error(err?.message || 'Failed to update'); }
  };

  const handleTransferOwnership = async () => {
    if (!transferTarget) { toast.error('Select a member'); return; }
    if (!confirm('Are you sure? This will transfer Super Owner role and demote you to Admin.')) return;
    try {
      await apiPost('/api/org/transfer-ownership', { new_owner_user_id: transferTarget });
      toast.success('Ownership transferred');
      setTransferDialogOpen(false);
      fetchOrgData();
    } catch (err) { toast.error(err?.message || 'Failed to transfer ownership'); }
  };

  const handleLeaveOrg = async () => {
    if (!confirm('Are you sure you want to leave this organization?')) return;
    try {
      await apiPost('/api/org/leave');
      toast.success('You have left the organization');
      navigate('/dashboard');
    } catch (err) { toast.error(err?.message || 'Failed to leave'); }
  };

  const handleUpdateModelPolicy = async (updates) => {
    setModelPolicyLoading(true);
    try {
      const body = { ...modelPolicy, ...updates };
      const data = await apiPut('/api/org/model-policy', body);
      setModelPolicy(prev => ({ ...prev, ...data }));
      toast.success('Model policy updated');
    } catch (err) { toast.error(err?.message || 'Failed to update model policy'); }
    finally { setModelPolicyLoading(false); }
  };

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) { toast.error('Key name is required'); return; }
    setKeyLoading(true);
    try {
      const data = await apiPost('/api/org/api-keys', {
        name: newKeyName.trim(), expires_in_days: parseInt(newKeyExpiry) || null,
      });
      setCreatedKey(data);
      setNewKeyName('');
      // Refresh list
      try {
        const d = await apiGet('/api/org/api-keys');
        setApiKeys(d.api_keys || []);
      } catch { /* ignore */ }
      toast.success('API key created');
    } catch (err) { toast.error(err?.message || 'Failed to create key'); }
    finally { setKeyLoading(false); }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!confirm('Revoke this API key? This cannot be undone.')) return;
    try {
      await apiDelete(`/api/org/api-keys/${keyId}`);
      setApiKeys(prev => prev.map(k => k.key_id === keyId ? { ...k, is_active: false } : k));
      toast.success('API key revoked');
    } catch (err) { toast.error(err?.message || 'Failed to revoke'); }
  };

  const handleSaveBranding = async () => {
    setBrandingLoading(true);
    try {
      const data = await apiPut('/api/org/branding', brandingDraft);
      setBranding(data.branding);
      setBrandingDraft(data.branding);
      toast.success('Branding updated');
    } catch (err) { toast.error(err?.message || 'Failed to update branding'); }
    finally { setBrandingLoading(false); }
  };

  const copyInviteLink = async (inviteToken) => {
    const link = `${window.location.origin}/join-org?token=${inviteToken}`;
    await navigator.clipboard.writeText(link);
    setCopiedLink(inviteToken);
    setTimeout(() => setCopiedLink(null), 2000);
    toast.success('Invite link copied!');
  };

  const getRoleIcon = (role) => {
    if (role === 'super_owner') return <Crown className="w-4 h-4 text-amber-500" />;
    if (role === 'admin') return <Shield className="w-4 h-4 text-blue-500" />;
    return <User className="w-4 h-4 text-slate-400" />;
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_owner: 'bg-amber-100 text-amber-700',
      admin: 'bg-blue-100 text-blue-700',
      user: 'bg-slate-100 text-slate-600',
      member: 'bg-slate-100 text-slate-600'
    };
    const labels = { super_owner: 'Super Owner', admin: 'Admin', user: 'Member', member: 'Member' };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[role] || styles.user}`}>
        {labels[role] || role}
      </span>
    );
  };

  const getActionLabel = (action) => {
    const labels = {
      org_created: 'Created organization', org_updated: 'Updated organization',
      member_invited: 'Invited member', member_joined: 'Member joined',
      member_removed: 'Removed member', member_left: 'Member left',
      role_changed: 'Changed role', billing_toggled: 'Toggled billing',
      ownership_transferred: 'Transferred ownership',
    };
    return labels[action] || action;
  };

  const isAdmin = org?.user_role === 'super_owner' || org?.user_role === 'admin';
  const isSuperOwner = org?.user_role === 'super_owner';
  const seats = org?.seats;

  const tabs = [
    { id: 'members', label: 'Members', icon: Users },
    { id: 'shared', label: 'Shared Analyses', icon: Share2 },
    ...(isAdmin ? [
      { id: 'settings', label: 'Org Settings', icon: SettingsIcon },
      { id: 'branding', label: 'Branding', icon: Palette },
      { id: 'apikeys', label: 'API Keys', icon: Key },
      { id: 'activity', label: 'Activity Log', icon: Activity },
    ] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-50/50">
      <Sidebar user={user} setUser={setUser} activePage="organization" />
      <main className={`pt-20 lg:pt-20 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        <div className="p-6 lg:p-8 max-w-5xl mx-auto">

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 font-outfit" data-testid="org-settings-title">Organization</h1>
                <p className="text-slate-500 text-sm">Manage your workspace, members, and permissions</p>
              </div>
            </div>
            <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit" data-testid="org-tabs">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => tab.id === 'members' ? setSearchParams({}) : setSearchParams({ tab: tab.id })}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid={`org-tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Shared Analyses Tab */}
          {activeTab === 'shared' ? <TeamSharedContent user={user} /> : activeTab === 'activity' ? (
            /* Activity Log Tab */
            <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-slate-900" data-testid="activity-log-title">Activity Log</h3>
              </div>
              <div className="divide-y">
                {activityLog.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No activity recorded yet</div>
                ) : activityLog.map((log, i) => (
                  <div key={log.log_id || i} className="px-4 py-3 flex items-start gap-3" data-testid={`activity-log-entry-${i}`}>
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Activity className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900">
                        <span className="font-medium">{log.actor_email}</span>{' '}
                        <span className="text-slate-600">{getActionLabel(log.action)}</span>
                      </p>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <p className="text-xs text-slate-500 mt-0.5">
                          {Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ')}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-slate-400 flex-shrink-0">
                      {new Date(log.created_at).toLocaleDateString()} {new Date(log.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : activeTab === 'settings' ? (
            /* Organization Settings Tab */
            <div className="space-y-6" data-testid="org-settings-tab">
              {/* AI Model Policy */}
              <div className="bg-white rounded-2xl p-6 border shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-100 to-purple-100 rounded-xl flex items-center justify-center">
                    <Cpu className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900" data-testid="model-policy-title">AI Model Policy</h3>
                    <p className="text-sm text-slate-500">Control which AI models your organization can use</p>
                  </div>
                </div>

                {modelPolicy ? (
                  <div className="space-y-5">
                    {/* Restrict Models Toggle */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Lock className="w-4 h-4 text-slate-500" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">Restrict Models</p>
                          <p className="text-xs text-slate-500">When enabled, members can only use allowed models</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleUpdateModelPolicy({ restrict_models: !modelPolicy.restrict_models })}
                        disabled={modelPolicyLoading}
                        className={`relative w-12 h-6 rounded-full transition-colors ${modelPolicy.restrict_models ? 'bg-blue-600' : 'bg-slate-300'}`}
                        data-testid="restrict-models-toggle"
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${modelPolicy.restrict_models ? 'translate-x-6' : ''}`} />
                      </button>
                    </div>

                    {/* Default Model */}
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">Organization Default Model</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {Object.entries(modelPolicy.available_models || {}).map(([id, model]) => {
                          const isDefault = modelPolicy.default_model === id;
                          const isAllowed = (modelPolicy.allowed_models || []).includes(id);
                          const icon = id === 'gemini-flash' ? <Zap className="w-5 h-5" /> : id === 'gpt-4o' ? <Sparkles className="w-5 h-5" /> : <Cpu className="w-5 h-5" />;
                          const color = id === 'gemini-flash' ? 'from-blue-500 to-cyan-500' : id === 'gpt-4o' ? 'from-green-500 to-emerald-500' : 'from-orange-500 to-amber-500';

                          return (
                            <button
                              key={id}
                              onClick={() => handleUpdateModelPolicy({ default_model: id })}
                              disabled={modelPolicyLoading || (modelPolicy.restrict_models && !isAllowed)}
                              className={`relative p-4 rounded-xl border-2 transition-all text-left ${isDefault ? 'border-blue-500 bg-blue-50/50 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-300'} ${(modelPolicy.restrict_models && !isAllowed) ? 'opacity-40 cursor-not-allowed' : ''}`}
                              data-testid={`default-model-${id}`}
                            >
                              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center text-white mb-2`}>
                                {icon}
                              </div>
                              <p className="text-sm font-semibold text-slate-900">{model.name}</p>
                              <p className="text-xs text-slate-500">{model.credits_per_use} credits/use</p>
                              {isDefault && <span className="absolute top-2 right-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Default</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Allowed Models */}
                    {modelPolicy.restrict_models && (
                      <div>
                        <Label className="text-sm font-medium text-slate-700 mb-2 block">Allowed Models</Label>
                        <p className="text-xs text-slate-500 mb-3">Members will only be able to select from these models</p>
                        <div className="space-y-2">
                          {Object.entries(modelPolicy.available_models || {}).map(([id, model]) => {
                            const isAllowed = (modelPolicy.allowed_models || []).includes(id);
                            return (
                              <label key={id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid={`allowed-model-${id}`}>
                                <input
                                  type="checkbox"
                                  checked={isAllowed}
                                  onChange={() => {
                                    const current = modelPolicy.allowed_models || [];
                                    const updated = isAllowed ? current.filter(m => m !== id) : [...current, id];
                                    if (updated.length === 0) { toast.error('At least one model must be allowed'); return; }
                                    handleUpdateModelPolicy({ allowed_models: updated });
                                  }}
                                  className="w-4 h-4 rounded text-blue-600"
                                />
                                <span className="text-sm font-medium text-slate-900">{model.name}</span>
                                <span className="text-xs text-slate-500 ml-auto">{model.credits_per_use} credits/use</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-24">
                    <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                  </div>
                )}
              </div>

              {/* Org Name */}
              <div className="bg-white rounded-2xl p-6 border shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-4">Organization Details</h3>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-lg font-semibold text-slate-900">{org?.name || 'My Organization'}</p>
                    <p className="text-sm text-slate-500 capitalize">{org?.plan_tier || 'Free'} Plan</p>
                  </div>
                  {isSuperOwner && (
                    <Button variant="outline" size="sm" onClick={() => setEditNameDialogOpen(true)} data-testid="edit-org-name-btn">
                      <Edit2 className="w-4 h-4 mr-1" /> Rename
                    </Button>
                  )}
                </div>
              </div>

              {/* Danger Zone */}
              {isSuperOwner && (
                <div className="bg-white rounded-2xl p-6 border border-red-200 shadow-sm">
                  <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Danger Zone
                  </h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-900">Transfer Ownership</p>
                      <p className="text-xs text-slate-500">Transfer Super Owner role to another admin</p>
                    </div>
                    <Button variant="destructive" size="sm" onClick={() => setTransferDialogOpen(true)} data-testid="transfer-ownership-btn">
                      <ArrowRightLeft className="w-4 h-4 mr-1" /> Transfer
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'branding' ? (
            /* Branding Tab */
            <div className="space-y-6" data-testid="branding-tab">
              <div className="bg-white rounded-2xl p-6 border shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-100 to-rose-100 rounded-xl flex items-center justify-center">
                    <Palette className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900" data-testid="branding-title">Organization Branding</h3>
                    <p className="text-sm text-slate-500">Customize your workspace appearance</p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Display Name</Label>
                    <Input
                      value={brandingDraft?.display_name || ''}
                      onChange={(e) => setBrandingDraft(prev => ({ ...prev, display_name: e.target.value }))}
                      placeholder="Organization display name"
                      maxLength={50}
                      data-testid="branding-display-name"
                    />
                    <p className="text-xs text-slate-500 mt-1">Shown in sidebar and headers</p>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Accent Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={brandingDraft?.accent_color || '#0B3D91'}
                        onChange={(e) => setBrandingDraft(prev => ({ ...prev, accent_color: e.target.value }))}
                        className="w-12 h-10 rounded-lg border border-slate-200 cursor-pointer"
                        data-testid="branding-accent-color"
                      />
                      <Input
                        value={brandingDraft?.accent_color || '#0B3D91'}
                        onChange={(e) => setBrandingDraft(prev => ({ ...prev, accent_color: e.target.value }))}
                        placeholder="#0B3D91"
                        className="w-32 font-mono text-sm"
                      />
                      <div className="flex gap-2">
                        {['#0B3D91', '#1E5EFF', '#6366F1', '#059669', '#DC2626', '#7C3AED', '#0891B2'].map(c => (
                          <button
                            key={c}
                            onClick={() => setBrandingDraft(prev => ({ ...prev, accent_color: c }))}
                            className="w-7 h-7 rounded-full border-2 transition-all hover:scale-110"
                            style={{ backgroundColor: c, borderColor: brandingDraft?.accent_color === c ? '#000' : 'transparent' }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Logo URL</Label>
                    <Input
                      value={brandingDraft?.logo_url || ''}
                      onChange={(e) => setBrandingDraft(prev => ({ ...prev, logo_url: e.target.value }))}
                      placeholder="https://example.com/logo.png"
                      data-testid="branding-logo-url"
                    />
                    <p className="text-xs text-slate-500 mt-1">Direct URL to your organization logo (max 500 characters)</p>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Preview</p>
                    <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white" style={{ borderLeftColor: brandingDraft?.accent_color || '#0B3D91', borderLeftWidth: '4px' }}>
                      {brandingDraft?.logo_url ? (
                        <img src={brandingDraft.logo_url} alt="Logo" className="w-10 h-10 rounded-lg object-cover" onError={(e) => { e.target.style.display='none'; }} />
                      ) : (
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: (brandingDraft?.accent_color || '#0B3D91') + '20' }}>
                          <Building2 className="w-5 h-5" style={{ color: brandingDraft?.accent_color || '#0B3D91' }} />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-slate-900">{brandingDraft?.display_name || org?.name || 'Your Organization'}</p>
                        <p className="text-xs text-slate-500 capitalize">{org?.plan_tier || 'Free'} Plan</p>
                      </div>
                    </div>
                  </div>

                  <Button onClick={handleSaveBranding} disabled={brandingLoading} data-testid="save-branding-btn">
                    {brandingLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                    Save Branding
                  </Button>
                </div>
              </div>
            </div>
          ) : activeTab === 'apikeys' ? (
            /* API Keys Tab */
            <div className="space-y-6" data-testid="api-keys-tab">
              <div className="bg-white rounded-2xl p-6 border shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center">
                    <Key className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900" data-testid="api-keys-title">API Keys</h3>
                    <p className="text-sm text-slate-500">Manage programmatic access to the Decoded API</p>
                  </div>
                </div>

                {createdKey && (
                  <div className="mb-5 p-4 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="created-key-banner">
                    <p className="text-sm font-semibold text-emerald-800 mb-2">API Key Created — Copy it now!</p>
                    <p className="text-xs text-emerald-600 mb-2">This key won&apos;t be shown again after you leave this page.</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-white/80 rounded-lg text-sm font-mono text-slate-800 break-all">{createdKey.api_key}</code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { navigator.clipboard.writeText(createdKey.api_key); setKeyCopied(true); setTimeout(() => setKeyCopied(false), 2000); }}
                        data-testid="copy-api-key-btn"
                      >
                        {keyCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Input
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="Key name (e.g., Production API)"
                    className="flex-1"
                    data-testid="new-key-name-input"
                  />
                  <select
                    value={newKeyExpiry}
                    onChange={(e) => setNewKeyExpiry(e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                    data-testid="new-key-expiry-select"
                  >
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="180">180 days</option>
                    <option value="365">1 year</option>
                    <option value="">Never</option>
                  </select>
                  <Button onClick={handleCreateApiKey} disabled={keyLoading} data-testid="create-api-key-btn">
                    <Plus className="w-4 h-4 mr-1" /> Create Key
                  </Button>
                </div>
              </div>

              <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
                <div className="p-4 border-b">
                  <h4 className="font-semibold text-slate-900">Active Keys</h4>
                </div>
                {apiKeys.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No API keys created yet</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {apiKeys.map((key) => (
                      <div key={key.key_id} className={`flex items-center justify-between p-4 ${!key.is_active ? 'opacity-50 bg-slate-50' : ''}`} data-testid={`api-key-${key.key_id}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${key.is_active ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                            <Key className={`w-4 h-4 ${key.is_active ? 'text-emerald-600' : 'text-slate-400'}`} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{key.name}</p>
                            <p className="text-xs text-slate-500">
                              <code className="font-mono">{key.prefix}...{'*'.repeat(20)}</code>
                              {' '}&middot;{' '}
                              Created {new Date(key.created_at).toLocaleDateString()} by {key.created_by_email}
                              {key.expires_at && (
                                <span className="ml-2 text-amber-600">Expires {new Date(key.expires_at).toLocaleDateString()}</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!key.is_active ? (
                            <span className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded-full">Revoked</span>
                          ) : (
                            <Button variant="ghost" size="sm" onClick={() => handleRevokeApiKey(key.key_id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`revoke-key-${key.key_id}`}>
                              <Trash2 className="w-4 h-4 mr-1" /> Revoke
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
          /* Members Tab */
          <>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">

              {/* Org Overview Card */}
              <div className="bg-white rounded-2xl p-6 border shadow-sm" data-testid="org-overview">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
                      <Building2 className="w-7 h-7 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-semibold text-slate-900" data-testid="org-name">{org?.name}</h2>
                        {isAdmin && (
                          <button onClick={() => setEditNameDialogOpen(true)} className="p-1 hover:bg-slate-100 rounded-lg transition-colors" data-testid="edit-org-name-btn">
                            <Edit2 className="w-4 h-4 text-slate-400" />
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">
                        {seats?.used_seats} of {seats?.total_seats} seat{seats?.total_seats !== 1 ? 's' : ''} used{' '}
                        <span className="capitalize font-medium text-blue-600">{org?.plan_tier} plan</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getRoleBadge(org?.user_role)}
                    {org?.has_billing && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <DollarSign className="w-3 h-3 inline -mt-0.5" /> Billing
                      </span>
                    )}
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-500">Members</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900" data-testid="member-count">{seats?.used_seats || 0}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-500">Pending</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900" data-testid="pending-invites-count">{invites.length}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-500">Available Seats</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900" data-testid="available-seats">{seats?.available_seats || 0}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 cursor-pointer hover:bg-blue-50 transition-colors" onClick={() => navigate('/billing')}>
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-500">Credits</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600" data-testid="org-credits-total">
                      {org?.credits?.total_available === -1 ? 'Unlimited' : (org?.credits?.total_available || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Members Table */}
              <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" data-testid="members-table">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">Members</h3>
                  <div className="flex gap-2">
                    {isSuperOwner && members.length > 1 && (
                      <Button variant="outline" size="sm" onClick={() => setTransferDialogOpen(true)} data-testid="transfer-ownership-btn">
                        <ArrowRightLeft className="w-4 h-4 mr-1" /> Transfer Ownership
                      </Button>
                    )}
                    {isAdmin && seats?.available_seats > 0 && (
                      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                        <DialogTrigger asChild>
                          <Button className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="invite-member-btn">
                            <UserPlus className="w-4 h-4" /> Invite
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Invite Member</DialogTitle>
                            <DialogDescription>Send an invitation to join your organization.</DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="invite-email">Email Address</Label>
                              <Input id="invite-email" type="email" placeholder="colleague@company.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid="invite-email-input" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="invite-role">Role</Label>
                              <Select value={inviteRole} onValueChange={setInviteRole}>
                                <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="member">
                                    <div className="flex items-center gap-2"><User className="w-4 h-4" /><span>Member</span><span className="text-slate-400 text-xs">- Can use features</span></div>
                                  </SelectItem>
                                  {isSuperOwner && (
                                    <SelectItem value="admin">
                                      <div className="flex items-center gap-2"><Shield className="w-4 h-4" /><span>Admin</span><span className="text-slate-400 text-xs">- Can manage members</span></div>
                                    </SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleInvite} disabled={inviteLoading} data-testid="send-invite-btn">
                              {inviteLoading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Mail className="w-4 h-4 mr-2" />Send Invitation</>}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                    {isAdmin && seats?.available_seats <= 0 && (
                      <Button variant="outline" onClick={() => navigate('/billing')} data-testid="upgrade-seats-btn">
                        Add More Seats
                      </Button>
                    )}
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Member</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Billing</TableHead>
                      <TableHead className="text-right">Credits Used</TableHead>
                      {isAdmin && <TableHead className="w-10"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member.user_id} data-testid={`member-row-${member.user_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center">
                              {getRoleIcon(member.role)}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{member.name}</p>
                              <p className="text-sm text-slate-500">{member.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{getRoleBadge(member.role)}</TableCell>
                        <TableCell>
                          {member.has_billing ? (
                            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Yes</span>
                          ) : (
                            <span className="text-xs text-slate-400">No</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">{member.credits_used}</TableCell>
                        {isAdmin && (
                          <TableCell>
                            {member.role !== 'super_owner' && member.user_id !== user?.user_id && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="p-2 hover:bg-slate-100 rounded-lg" data-testid={`member-actions-${member.user_id}`}>
                                    <MoreVertical className="w-4 h-4 text-slate-400" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {isSuperOwner && (member.role === 'user' || member.role === 'member') && (
                                    <DropdownMenuItem onClick={() => handleUpdateRole(member.user_id, 'admin')}>
                                      <Shield className="w-4 h-4 mr-2" /> Promote to Admin
                                    </DropdownMenuItem>
                                  )}
                                  {isSuperOwner && member.role === 'admin' && (
                                    <DropdownMenuItem onClick={() => handleUpdateRole(member.user_id, 'member')}>
                                      <User className="w-4 h-4 mr-2" /> Demote to Member
                                    </DropdownMenuItem>
                                  )}
                                  {isSuperOwner && (
                                    <DropdownMenuItem onClick={() => handleToggleBilling(member.user_id, member.has_billing)}>
                                      <DollarSign className="w-4 h-4 mr-2" /> {member.has_billing ? 'Revoke Billing' : 'Grant Billing'}
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem className="text-red-600" onClick={() => handleRemoveMember(member.user_id, member.name)}>
                                    <Trash2 className="w-4 h-4 mr-2" /> Remove
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pending Invites */}
              {isAdmin && invites.length > 0 && (
                <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" data-testid="pending-invites">
                  <div className="p-4 border-b">
                    <h3 className="font-semibold text-slate-900">Pending Invitations</h3>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead className="w-20"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invites.map((invite) => (
                        <TableRow key={invite.invite_id} data-testid={`invite-row-${invite.invite_id}`}>
                          <TableCell className="font-medium">{invite.email}</TableCell>
                          <TableCell>{getRoleBadge(invite.role)}</TableCell>
                          <TableCell className="text-slate-500">{new Date(invite.expires_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <button onClick={() => copyInviteLink(invite.token)} className="p-2 hover:bg-slate-100 rounded-lg" title="Copy link">
                                {copiedLink === invite.token ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-slate-400" />}
                              </button>
                              <button onClick={() => handleCancelInvite(invite.invite_id)} className="p-2 hover:bg-red-50 rounded-lg" title="Cancel">
                                <X className="w-4 h-4 text-red-500" />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Leave Org (non-super_owner only) */}
              {!isSuperOwner && (
                <div className="flex justify-end">
                  <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleLeaveOrg} data-testid="leave-org-btn">
                    Leave Organization
                  </Button>
                </div>
              )}

              {/* Seat Limit Warning */}
              {seats?.available_seats <= 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="seat-limit-warning">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Seat limit reached</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Your {org?.plan_tier} plan allows {seats?.total_seats} seat{seats?.total_seats !== 1 ? 's' : ''}
                        {seats?.additional_seats > 0 ? ` (${seats?.included_seats} included + ${seats?.additional_seats} additional)` : ''}.
                        {' '}Add more seats from the billing page.
                      </p>
                      <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/billing')}>
                        Manage Seats
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          </>
          )}

          {/* Edit Name Dialog */}
          <Dialog open={editNameDialogOpen} onOpenChange={setEditNameDialogOpen}>
            <DialogContent>
              <DialogHeader><DialogTitle>Edit Organization Name</DialogTitle></DialogHeader>
              <div className="py-4">
                <Label htmlFor="orgName">Organization Name</Label>
                <Input id="orgName" value={newOrgName} onChange={(e) => setNewOrgName(e.target.value)} className="mt-2" data-testid="org-name-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditNameDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleUpdateOrgName} data-testid="save-org-name-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Transfer Ownership Dialog */}
          <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Transfer Ownership</DialogTitle>
                <DialogDescription>Transfer the Super Owner role to another member. You will be demoted to Admin.</DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-2">
                <Label>Select New Owner</Label>
                <Select value={transferTarget} onValueChange={setTransferTarget}>
                  <SelectTrigger data-testid="transfer-target-select"><SelectValue placeholder="Choose a member" /></SelectTrigger>
                  <SelectContent>
                    {members.filter(m => m.user_id !== user?.user_id).map(m => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        <div className="flex items-center gap-2">{getRoleIcon(m.role)} {m.name} ({m.email})</div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
                <Button className="bg-red-600 hover:bg-red-700" onClick={handleTransferOwnership} data-testid="confirm-transfer-btn">Transfer Ownership</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
};

export default OrgSettings;
